import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { ArrowLeft, Mail, Loader2, CheckCircle } from 'lucide-react';
import axios from 'axios';

// V2 Forgot Password — for unified_users. Differs from the legacy page
// by NOT requiring a Client ID (unified users have one email across all
// tenants). Same visual language as the legacy flow to keep muscle
// memory intact.
const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const V2ForgotPassword = () => {
  const { theme, setTheme } = useTheme();
  const previousThemeRef = useRef(theme);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [email, setEmail] = useState('');

  useEffect(() => {
    previousThemeRef.current = theme;
    if (theme !== 'dark') setTheme('dark');
    return () => {
      if (previousThemeRef.current !== 'dark') setTheme(previousThemeRef.current);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) {
      toast.error('Please enter your email address');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API_URL}/v2/auth/forgot-password`, {
        email: email.toLowerCase().trim(),
      });
      setSubmitted(true);
    } catch (error) {
      // Backend returns 200 even for unknown email — a real error here
      // is network/server-side.
      toast.error(error.response?.data?.detail || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center p-4 bg-cover bg-center relative"
      style={{
        backgroundImage: 'url(https://customer-assets.emergentagent.com/job_job-tracker-app-6/artifacts/penuw891_iStock-1174890965.jpg)',
      }}
    >
      <div className="absolute inset-0 bg-black/60" />

      <Card className="w-full max-w-md relative z-10 border-0 shadow-2xl" data-testid="v2-forgot-password-card">
        <CardHeader className="text-center pb-2">
          <div className="flex justify-center mb-4">
            <div className="w-16 h-16 rounded-xl overflow-hidden">
              <img src="/equipr-logo.png" alt="EQUIPR" className="w-full h-full object-cover" />
            </div>
          </div>
          <CardTitle className="font-heading text-2xl">
            {submitted ? 'Check Your Email' : 'Forgot Password'}
          </CardTitle>
          <CardDescription>
            {submitted
              ? 'If an account exists with that email, we sent you a reset link.'
              : 'Enter your email and we\'ll send you a reset link'}
          </CardDescription>
        </CardHeader>

        <CardContent>
          {submitted ? (
            <div className="text-center space-y-6">
              <div className="flex justify-center">
                <div className="w-16 h-16 rounded-full bg-green-500/20 flex items-center justify-center">
                  <CheckCircle className="h-8 w-8 text-green-500" />
                </div>
              </div>

              <div className="space-y-2">
                <p className="text-sm text-muted-foreground">
                  We sent a password reset link to <strong>{email}</strong>
                </p>
                <p className="text-sm text-muted-foreground">
                  The link will expire in <strong>60 minutes</strong>.
                </p>
              </div>

              <div className="space-y-3 pt-4">
                <p className="text-xs text-muted-foreground">
                  Didn&apos;t receive the email? Check your spam folder or try again.
                </p>
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={() => setSubmitted(false)}
                  data-testid="v2-try-again-button"
                >
                  Try Again
                </Button>
              </div>

              <div className="pt-4 border-t">
                <Link
                  to="/login"
                  className="text-sm text-muted-foreground hover:text-foreground transition-colors inline-flex items-center gap-1"
                  data-testid="v2-back-to-login-link"
                >
                  <ArrowLeft className="h-4 w-4" />
                  Back to login
                </Link>
              </div>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label className="label-overline">Email Address</Label>
                <Input
                  data-testid="v2-email-input"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="your.email@company.com"
                  required
                  autoFocus
                />
                <p className="text-xs text-muted-foreground">
                  Enter the email address associated with your EQUIPR account
                </p>
              </div>

              <Button
                type="submit"
                className="w-full btn-volt"
                disabled={loading}
                data-testid="v2-submit-button"
              >
                {loading ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Mail className="h-4 w-4 mr-2" />
                    Send Reset Link
                  </>
                )}
              </Button>

              <div className="pt-4 border-t text-center">
                <Link
                  to="/login"
                  className="text-sm text-muted-foreground hover:text-foreground transition-colors inline-flex items-center gap-1"
                  data-testid="v2-back-to-login-link"
                >
                  <ArrowLeft className="h-4 w-4" />
                  Back to login
                </Link>
              </div>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default V2ForgotPassword;
